import { Address } from "@/models/address";
import { icons } from "@/utils/icons";

export type InstanceConfiguration = {
    corporateName: string,
    mode: {
        marketplace: boolean,
        b2b: boolean,
        multilanguage: boolean,
        productDisplay: ProductDisplayMode
    },
    login: {
        showSignIn: boolean,
        modal: {
            showActions: boolean
        }
    },
    menu: {
        showServiceProvidersLink: boolean,
        showPromotionsLink: boolean,
        showContactLink: boolean
    }
    header: {
        icons: {
            showQuickOrder: boolean,
            showCart: boolean,
            showBecomeSeller: boolean,
            showAccount: boolean,
            showContact: boolean,
            showRootCategory: boolean,
        },
        showSupport: boolean,
        supportTelNr: string,
        rootCategory: {
            enabled: boolean,
            modal: {
                showAsSelector: boolean,
                showAsList: boolean,
                showFocused: boolean,
                closeOnNewSelection: boolean
            }
        },
        topBanner: {
            enabled: boolean,
            showPromoItems: boolean,
            staticItems: TopBannerItem[]
        }
    },
    home: {
        promotions: {
            slidesPerView: {
                xl: number,
                large: number,
                medium: number,
                small: number
            }
        }
    },
    user: {
        address: {
            showName: boolean
            showDetails: boolean
        },
        orders: {
            backToOrdersButtonClasses: string[],
            showEventHistory: boolean,
            orderNamingEnabled: boolean
            orderValidityEnabled: boolean
        }
    },
    category: {
        sortCategoriesAlphabetically: boolean,
        showMarketing: boolean,
        showConfigureButton: boolean
    }
    productList: {
        desktopPagingMode: ProductsPagingMode,
        autoLoadNextProducts: boolean,
        infiniteScrollOffset: number,
        navbar: {
            show: boolean,
            showDisplayModeSelection: boolean
            defaultDisplayMode: ProductsDisplayMode,
            defaultSort: ProductsSortProperty | string,
            defaultSortOrder: ProductsSortOrder,
            defaultItemsPerPage: number
        },
        sidebar: {
            show: boolean
        }
        filters: {
            useSwatch: boolean,
            alphabet: {
                show: boolean,
                attributeName: string
            }
        }
        category: {
            showCategoriesPage: boolean,
            minLevelToShowProducts: number
        }
    }
    product: {
        showStockNotice: boolean,
        showShareButton: boolean,
        showWishListButton: boolean,
        pictureIconFlags: ProductPictureIconFlag[],
        attributeIcons: AttributeIcon[]
    },
    cart: {
        coupon: {
            enabled: boolean
        },
        addToCart: {
            showIcon: boolean
        },
        sidebar: {
            buttons: null
        }
    },
    checkout: {
        shipping: {
            showProduct: boolean,
            showSidebar: boolean,
            showFranco: boolean,
            showEstimatedDeliveryTime: boolean,
            enableAddressModification: boolean,
            estimatedDateStrategy: 'NUMBER' | 'PERIOD',
            isAddressEligible: (address: Address) => boolean,
            notEligibleAddressMessage: (address: Address) => string,
            noEligibleAddressMessage: () => string
        },
        payment: {
            showProduct: boolean,
            showSidebar: boolean
        },
        thanks: {
            goToHomeButtonClasses: string[],
            goToOrderButtonClasses: string[]
        }
    },
    forms :{
        buttons: {
            forgotPassword: {
                classes : string[]
            },
            connect: {
                classes : string[]
            },
            submit: {
                classes : string[]
            },
            goToCart: {
                classes: string[]
            },
            goToCheckout: {
                classes: string[]
            },
            backToHome: {
                classes: string[]
            },
            goToMyAccount: {
                classes: string[]
            },
            clearCart: {
                classes: string[]
            },
            continueShopping: {
                classes: string[]
            },
            editAddress: {
                classes: string[]
            }
        },
        captcha: {
            showNotice: boolean
        }
    },
    navigation: {
        catalogChange: {
            returnToHome: boolean
        }
    },
    search: {
        requireAuth: boolean
    },
    footer: {
        social: SvgIconLink[],
        menuCol1: FooterMenuColumn | null,
        menuCol2: FooterMenuColumn | null,
        menuCol3: FooterMenuColumn | null,
        menuAnnexe: FooterMenuColumn | null,
        newsletter: {
            enabled: boolean,
            strategy: NewsletterFooterDisplayStrategy
        },
        showNewsletter: boolean,
        showFooterReassurance: boolean,
        reassuranceIcons: IconLink[],
        contactInfo: FooterContactInfo | null,
    },
    welcomeModal: {
        showAfterLogin: boolean
    }
}

export enum ProductDisplayMode {
    CONFIGURATION,
    VARIATION_LISTING
}

export enum ProductsPagingMode {
    PAGES,
    INFINITE_SCROLL
}

export enum ProductsDisplayMode {
    LIST = 'list',
    LARGE = 'large',
    SMALL = 'small'
}

export enum ProductsSortProperty {
    SCORE = 'score',
    PRICE = 'price'
}

export enum ProductsSortOrder {
    ASC = 'asc',
    DESC = 'desc'
}

export enum NewsletterFooterDisplayStrategy {FULL_WIDTH_BEFORE_COLUMNS, INSIDE_LAST_COLUMN}

export interface TopBannerItem {
    id: string,
    isActive: boolean
    titleKey: string,
    buttonLabelKey?: string,
    buttonUrl?: string
}

export interface PromoTopBannerItem extends TopBannerItem {
    startDate: Date,
    endDate: Date,
    isEvent: boolean,
}

export interface ProductPictureIconFlag {
    classSuffix: string,
    attributeId: string,
    labelKey: string,
    iconUrl: string
}

export interface AttributeIcon {
    attributeName: string,
    iconType: 'jpg' | 'png' | 'svg'
}

export interface IconLink {
    label: string,
    iconSvg?: SvgIconData
    iconUrl?: string
    link?: string
}

export interface SvgIcon {
    label: string,
    icon: SvgIconData
}

export interface SvgIconLink extends SvgIcon {
    link?: string
}

export interface FooterMenuColumn {
    slug: string | null,
    items: FooterMenuItem[];
}
export interface FooterContactInfo {
    email: string | null,
    tel: string | null;
}

export interface FooterMenuItem {
    slug: string,
    url: string,
    i18nSlug?: string,
    moqSlug?: string,
    target?: '_self' | '_blank'
    type?: FooterMenuItemType,
    component?: unknown
}

export enum FooterMenuItemType {
    GENERIC,
    INTERNAL,
    EXTERNAL
}

export interface SvgIconData {
    viewBox: string,
    path: string | string[]
}



export const DEFAULT_CONFIGURATION: InstanceConfiguration = {
    corporateName: "Demo application",
    mode: {
        marketplace: false,
        b2b: false,
        multilanguage: false,
        productDisplay: ProductDisplayMode.VARIATION_LISTING
    },
    login: {
        showSignIn: true,
        modal: {
            showActions: true
        }
    },
    menu: {
        showContactLink: true,
        showPromotionsLink: true,
        showServiceProvidersLink: true
    },
    header: {
        icons: {
            showCart: true,
            showAccount: true,
            showBecomeSeller: true,
            showContact: true,
            showQuickOrder: true,
            showRootCategory: true
        },
        showSupport: true,
        supportTelNr: "+33 (0)7 77 77 77 77",
        rootCategory: {
            enabled: true,
            modal: {
                showAsSelector: true,
                showAsList: true,
                showFocused: true,
                closeOnNewSelection: false
            }
        },
        topBanner: {
            enabled: false,
            showPromoItems: false,
            staticItems: []
        }
    },
    home: {
        promotions: {
            slidesPerView: {
                xl: 4,
                large: 3,
                medium: 2,
                small: 1
            }
        }
    },
    user: {
        address: {
            showName: false,
            showDetails: true
        },
        orders: {
            backToOrdersButtonClasses: ["sf-button--text", "color-primary", "return"],
            showEventHistory: true,
            orderNamingEnabled: false,
            orderValidityEnabled: false
        }
    },
    category: {
        sortCategoriesAlphabetically: true,
        showMarketing: false,
        showConfigureButton: true
    },
    productList: {
        desktopPagingMode: ProductsPagingMode.PAGES,
        autoLoadNextProducts: false,
        infiniteScrollOffset: 64,
        navbar: {
            show: true,
            showDisplayModeSelection: true,
            defaultDisplayMode: ProductsDisplayMode.LIST,
            defaultSort: ProductsSortProperty.SCORE,
            defaultSortOrder: ProductsSortOrder.DESC,
            defaultItemsPerPage: 50
        },
        sidebar: {
            show: true
        },
        filters: {
            useSwatch: false,
            alphabet: {
                show: false,
                attributeName: 'product_first_character'
            }
        },
        category: {
            showCategoriesPage: true,
            minLevelToShowProducts: 1
        }
    },
    product: {
        showStockNotice: true,
        showShareButton: true,
        showWishListButton: true,
        pictureIconFlags: [],
        attributeIcons: []
    },
    cart: {
        coupon: {
            enabled: true
        },
        addToCart: {
            showIcon: false
        },
        sidebar: {
            buttons: null
        }
    },
    checkout: {
        shipping: {
            showProduct: false,
            showSidebar: true,
            showFranco: false,
            showEstimatedDeliveryTime: true,
            enableAddressModification: true,
            estimatedDateStrategy: "NUMBER",
            isAddressEligible: () => true,
            notEligibleAddressMessage: () => "",
            noEligibleAddressMessage: () => ""
        },
        payment: {
            showProduct: true,
            showSidebar: true
        },
        thanks: {
            goToHomeButtonClasses: ['color-white-bg', 'text-secondary-color'],
            goToOrderButtonClasses: ['color-secondary']
        }
    },
    forms: {
        buttons: {
            forgotPassword: {
                classes: ["color-white"]
            },
            connect: {
                classes: ["color-white"]
            },
            submit: {
                classes: ["color-secondary"]
            },
            goToCart: {
                classes: ["color-primary"]
            },
            goToCheckout: {
                classes: ["color-secondary"]
            },
            backToHome: {
                classes: ["color-secondary"]
            },
            goToMyAccount: {
                classes: ["color-secondary"]
            },
            clearCart: {
                classes: ["color-white"]
            },
            continueShopping: {
                classes: ["color-primary"]
            },
            editAddress: {
                classes: ["color-secondary"]
            }
        },
        captcha: {
            showNotice: true
        }
    },
    navigation: {
        catalogChange: {
            returnToHome: true
        }
    },
    search: {
        requireAuth: false
    },
    footer: {
        newsletter: {
            enabled: true,
            strategy: NewsletterFooterDisplayStrategy.INSIDE_LAST_COLUMN
        },
        showNewsletter: true,
        showFooterReassurance: true,
        social: [
            {
                label: 'facebook',
                link: 'https://www.facebook.com/',
                icon: icons.facebook
            },
            {
                label: 'twitter',
                link: '',
                icon: icons.twitter
            },
            {
                label: 'instagram',
                link: 'https://www.instagram.com/',
                icon: icons.instagram
            },
            {
                label: 'linkedIn',
                link: '',
                icon: icons.linkedIn
            }
        ],
        menuCol1: {
            slug: "about",
            items: [
                {
                    url: 'devenir-vendeur',
                    slug: 'become-seller'
                },
                {
                    url: 'guide-d-achat',
                    slug: 'buying-guide',
                },
                {
                    url: 'ui-sommes-nous',
                    slug: 'about-us'
                },
            ]
        },
        menuCol2: {
            slug: "customer-service",
            items: [
                {
                    url: '/mon-compte/commandes',
                    slug: 'my-orders',
                    type: FooterMenuItemType.INTERNAL
                },
                {
                    url: '/contact',
                    slug: 'contact',
                    type: FooterMenuItemType.INTERNAL
                }, {
                    url: 'politique-de-retours',
                    slug: 'return-policy'
                }
            ]
        },
        menuCol3: {
            slug: "information",
            items: [
                {
                    url: 'conditions-generales-de-vente',
                    slug: 'general-terms',
                    target: '_blank'
                },
                {
                    url: 'gestion-des-cookies',
                    slug: 'cookies-management',
                },
                {
                    url: 'mentions-legales',
                    slug: 'legal-notices'
                },
                {
                    url: 'politique-de-protection-des-donnees-personnelles',
                    slug: 'privacy-policy',
                    target: '_blank'
                },
            ]
        },
        menuAnnexe: null,
        reassuranceIcons: [
            {
                label: 'components.orkReassurance.securedPaiement',
                iconSvg: icons.SecurePayment,
            },
            {
                label: 'components.orkReassurance.fastDelivery',
                iconSvg: icons.freeDelivery,
            },
            {
                label: 'components.orkReassurance.localProducts',
                iconSvg: icons.pin,
            },
            {
                label: 'components.orkReassurance.coldChainRespect',
                iconSvg: icons.temperature,
            }
        ],
        contactInfo: null
    },
    welcomeModal: {
        showAfterLogin: true
    }
}

export const configuration: InstanceConfiguration = DEFAULT_CONFIGURATION;